import { createContext, useState, useEffect, useContext, type ReactNode } from 'react';

// Define theme context type
interface ThemeContextType {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  isDark: boolean;
}

// Create a theme context with sensible defaults
const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  toggleTheme: () => {},
  isDark: false,
});

interface ThemeProviderProps {
  children: ReactNode;
}

// Theme provider component
export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  // Check if theme is stored in localStorage, default to 'light'
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const savedTheme = localStorage.getItem('wayne-ai-theme');
    if (savedTheme === 'dark' || savedTheme === 'light') {
      return savedTheme;
    }
    // Respect system preference if no stored preference
    try {
      if (window.matchMedia?.('(prefers-color-scheme: dark)')?.matches) {
        return 'dark';
      }
    } catch {
      // matchMedia not available (e.g., JSDOM in tests)
    }
    return 'light';
  });

  // Effect to update DOM attributes and persist preference. The CSS-var
  // theme bridge in `src/styles/globals.css` reads `data-theme` directly,
  // so flipping that attribute is enough to swap the entire palette.
  useEffect(() => {
    // Set data-theme attribute on the document element for CSS variable switching
    document.documentElement.setAttribute('data-theme', theme);

    // Update body classes for global styling hooks
    document.body.classList.remove('wayne-light', 'wayne-dark');
    document.body.classList.add(`wayne-${theme}`);

    // Persist theme preference
    localStorage.setItem('wayne-ai-theme', theme);
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    let mediaQuery: MediaQueryList | undefined;
    try {
      mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      return;
    }
    if (!mediaQuery || !mediaQuery.addEventListener) return;
    const handleChange = (e: MediaQueryListEvent) => {
      const storedTheme = localStorage.getItem('wayne-ai-theme');
      if (!storedTheme) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery!.removeEventListener('change', handleChange);
  }, []);

  // Toggle between light and dark themes
  const toggleTheme = () => {
    setTheme(prevTheme => {
      const nextTheme = prevTheme === 'light' ? 'dark' : 'light';
      window.dispatchEvent(new CustomEvent('wayne:theme-changed', { detail: { theme: nextTheme } }));
      return nextTheme;
    });
  };

  const isDark = theme === 'dark';

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
